import { useMutation, useQuery } from '@apollo/client'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'

import useLocationInfo from 'pared/components/LocationInfo/hooks/useLocationInfo'
import Spin from 'pared/components/basicUi/spin'
import COLORS from 'pared/constants/colors'
import { MOBILE_WIDTH } from 'pared/constants/styles'
import { getUser } from 'pared/utils/user'

import { IAiBlackboxProps } from '../../type'
import PrepGuide from './components/PrepGuide'
import PrepGuideSummaryModal from './components/PrepGuideSummaryModal'
import SalesForecast from './components/SalesForecast'
import {
  IDeliveryDayPrepItemsType,
  IPrepItemSubmitRecordType,
  IRecordDataType,
  IStoreDeliveryDaysType,
  ISubmitPrepItemRecordVariables,
  getDeliveryDayPrepItems,
  getDeliveryScheduleLocations,
  getPrepItemSubmitRecords,
  submitPrepItemRecord,
} from './gql'
import usePrepItemWeekdayDataSource from './hooks/usePrepItemWeekdayDataSource'
import {
  calculatePrepItemValues,
  transformHistoricalItem,
} from './utils/prepItemCalculations'
import { printPrepGuide } from './utils/printPDF'

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 40px;
  width: 100%;
  max-width: 1200px;

  @media ${MOBILE_WIDTH} {
    flex-direction: column;
    width: 100vw;
    padding: 0 15px;
    gap: 10px;
    box-sizing: border-box;
  }
`

const Button = styled.button<{ variant: 'primary' | 'secondary' | 'tertiary' }>`
  padding: 16px 40px;
  border-radius: 8px;
  font-family: Lexend-SemiBold;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1;
  max-width: 300px;

  ${(props) =>
    props.variant === 'primary' &&
    `
    background-color: ${COLORS.Plum};
    color: white;
    border: none;

    &:hover {
      background-color: #3a7bc8;
    }

    &:active {
      transform: translateY(1px);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      &:hover {
        background-color: ${COLORS.Plum};
      }
    }
  `}

  ${(props) =>
    props.variant === 'secondary' &&
    `
    background-color: #a8c4a2;
    color: white;
    border: none;

    &:hover {
      background-color: #96b58f;
    }

    &:active {
      transform: translateY(1px);
    }
  `}

  ${(props) =>
    props.variant === 'tertiary' &&
    `
    background-color: white;
    color: ${COLORS.Chalkboard};
    border: 2px solid #e0e0e0;

    &:hover {
      background-color: #f5f5f5;
    }

    &:active {
      transform: translateY(1px);
    }
  `}

  @media ${MOBILE_WIDTH} {
    max-width: 100%;
  }
`

const DeliveryDayBanner = styled.div`
  background-color: #e3f2fd;
  border: 1px solid #1976d2;
  border-radius: 8px;
  padding: 12px 20px;
  margin-bottom: 20px;
  width: 100%;
  min-width: 800px;
  max-width: 1200px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${COLORS.Chalkboard};

  @media ${MOBILE_WIDTH} {
    width: 100vw;
    min-width: initial;
    padding: 10px 15px;
    flex-wrap: wrap;
  }
`

const DayBeforeBanner = styled.div`
  background-color: #fff8e1;
  border: 1px solid #f9a825;
  border-radius: 8px;
  padding: 12px 20px;
  margin-bottom: 20px;
  width: 100%;
  min-width: 800px;
  max-width: 1200px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${COLORS.Chalkboard};

  @media ${MOBILE_WIDTH} {
    width: 100vw;
    min-width: initial;
    padding: 10px 15px;
    flex-wrap: wrap;
  }
`

const DayChip = styled.span<{ active?: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(props) => (props.active ? '#1976d2' : '#e0e0e0')};
  color: ${(props) => (props.active ? 'white' : COLORS.Chalkboard)};
`

const PrepItemCalculator = ({
  startDate,
  queryFilter,
  setDate,
}: IAiBlackboxProps) => {
  const expoUserInfo = getUser()
  const userId = expoUserInfo?.userId
  const locationId = queryFilter?.locationIds?.[0] || 0
  const locationInfo = useLocationInfo(locationId)
  const [adjustment, setAdjustment] = React.useState(0)
  const [showSummaryModal, setShowSummaryModal] = React.useState(false)
  const [onHandValues, setOnHandValues] = React.useState<
    Record<string, Record<string, number>>
  >({})
  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const loadedLocationDateKeyRef = React.useRef<string | null>(null)
  // why: tracks whether the user has typed in the Adjustment field for the
  // current location+date, so the global-default pre-fill doesn't overwrite a
  // value they entered (standard dirty-input pattern).
  const adjustmentEditedRef = React.useRef(false)

  const startDateMoment = moment(startDate).isValid() ? moment(startDate) : null

  // Delivery day data
  const { data: deliveryLocationsData } = useQuery<IStoreDeliveryDaysType>(
    getDeliveryScheduleLocations,
  )
  const { data: deliveryDayPrepItemsData } =
    useQuery<IDeliveryDayPrepItemsType>(getDeliveryDayPrepItems)

  const storeDeliveryDays = useMemo(() => {
    if (!locationId || !deliveryLocationsData?.getDeliveryScheduleLocations)
      return []
    const loc = deliveryLocationsData.getDeliveryScheduleLocations.nodes.find(
      (n) => n.locationId === locationId,
    )
    if (!loc) return []
    return loc.deliveryDays.split(', ')
  }, [locationId, deliveryLocationsData])

  const isDeliveryDay = useMemo(() => {
    if (!startDateMoment || storeDeliveryDays.length === 0) return false
    const dayAbbrev = startDateMoment.format('ddd') // "Mon", "Tue", etc.
    return storeDeliveryDays.some(
      (d) => d.toLowerCase() === dayAbbrev.toLowerCase(),
    )
  }, [startDateMoment, storeDeliveryDays])

  const isDayBeforeDelivery = useMemo(() => {
    if (!startDateMoment || storeDeliveryDays.length === 0) return false
    const tomorrow = startDateMoment.clone().add(1, 'day')
    const tomorrowAbbrev = tomorrow.format('ddd')
    return storeDeliveryDays.some(
      (d) => d.toLowerCase() === tomorrowAbbrev.toLowerCase(),
    )
  }, [startDateMoment, storeDeliveryDays])

  const hasDeliveryDays = storeDeliveryDays.length > 0

  const deliveryDayNumbers = useMemo(() => {
    const map: Record<string, number> = {
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6,
    }
    const set = new Set<number>()
    for (const d of storeDeliveryDays) {
      const n = map[d.toLowerCase()]
      if (n !== undefined) set.add(n)
    }
    return set
  }, [storeDeliveryDays])

  const deliverySafeItemIds = useMemo(
    () =>
      new Set<string>(
        (deliveryDayPrepItemsData?.getDeliveryDayPrepItems?.nodes ?? []).map(
          (n) => n.prepItemId,
        ),
      ),
    [deliveryDayPrepItemsData],
  )

  const redistributionEnabled = hasDeliveryDays

  const {
    dataSource: prepItemWeekdayDataSource,
    nonPrepDataSource: prepItemNonPrepDataSource,
    loading: prepItemWeekdayLoading,
    totalSalesForecast,
    weeklySalesForecast,
    prepAmountIncreasePercent,
  } = usePrepItemWeekdayDataSource(
    locationId || 0,
    startDateMoment,
    deliveryDayNumbers,
    deliverySafeItemIds,
    redistributionEnabled,
  )

  const [submitRecord] = useMutation<void, ISubmitPrepItemRecordVariables>(
    submitPrepItemRecord,
  )

  const isToday = startDateMoment?.isSame(moment(), 'day') || false
  const isBeforeToday = startDateMoment?.isBefore(moment(), 'day') || false

  // Query for historical records when date is before today
  const { data: historicalData, loading: historicalLoading } =
    useQuery<IPrepItemSubmitRecordType>(getPrepItemSubmitRecords, {
      variables: {
        locationId: locationId || 0,
        reportDate: startDate || '',
      },
      skip: !locationId || !startDate,
      fetchPolicy: 'network-only',
    })

  // Extract first historical record if available
  const historicalRecord = historicalData?.getPrepItemSubmitRecords?.nodes?.[0]

  // Load historical data when available (only once per location+date to avoid overwriting user edits)
  const locationDateKey = `${locationId}-${startDate}`
  useEffect(() => {
    if (historicalLoading) return // Wait for query to finish before acting

    if (loadedLocationDateKeyRef.current !== locationDateKey) {
      loadedLocationDateKeyRef.current = locationDateKey
      if (historicalRecord?.record) {
        setAdjustment(historicalRecord.record.adjustment || 0)
        setOnHandValues(historicalRecord.record.onHandValues || {})
        // why: a saved record carries its own frozen adjustment, so suppress the
        // global-default pre-fill for it.
        adjustmentEditedRef.current = true
        setIsSubmitted(true) // Historical records are already submitted
      } else {
        // why: ENG-4793 — pre-fill the visible Sales Forecast Adjustment field
        // with the global default value so the store sees the inherited value
        // they can override. Falls back to 0 if global hasn't loaded yet; a
        // follow-up effect syncs once it arrives.
        setAdjustment(prepAmountIncreasePercent ?? 0)
        setOnHandValues({})
        adjustmentEditedRef.current = false
        setIsSubmitted(false)
      }
    }
  }, [
    historicalRecord,
    historicalLoading,
    locationDateKey,
    prepAmountIncreasePercent,
  ])

  // why: when the global default arrives async after the initial reset,
  // backfill the Sales Forecast Adjustment field if the user hasn't typed
  // anything yet (adjustment still at 0 sentinel) and we're not viewing
  // a historical record.
  const globalSyncedRef = React.useRef(false)
  useEffect(() => {
    if (globalSyncedRef.current) return
    if (
      !historicalRecord?.record &&
      prepAmountIncreasePercent !== undefined &&
      prepAmountIncreasePercent > 0 &&
      adjustment === 0
    ) {
      setAdjustment(prepAmountIncreasePercent)
      globalSyncedRef.current = true
    }
  }, [prepAmountIncreasePercent, historicalRecord, adjustment])

  // why: the consolidated Adjustment field defaults to the global Prep Amount
  // Increase from prep_settings. Pre-fill it once that setting resolves, unless
  // the user has already typed a value or a frozen historical record is loaded.
  useEffect(() => {
    if (historicalLoading) return
    if (adjustmentEditedRef.current) return
    if (historicalRecord?.record) return
    setAdjustment(prepAmountIncreasePercent || 0)
  }, [prepAmountIncreasePercent, historicalRecord, historicalLoading])

  const handleAdjustmentChange = (value: number) => {
    adjustmentEditedRef.current = true
    setAdjustment(value)
  }

  // For past dates, show frozen historical data (read-only view of what was submitted)
  // For today/future, use live data so user edits are reflected in real-time
  const frozenRecord = isBeforeToday ? historicalRecord?.record : null
  const displayPrepItems = frozenRecord?.items
    ? frozenRecord.items.map(transformHistoricalItem)
    : prepItemWeekdayDataSource
  const displayNonPrepItems = frozenRecord?.nonPrepItems
    ? frozenRecord.nonPrepItems.map(transformHistoricalItem)
    : prepItemNonPrepDataSource
  const displayBaseForecast = frozenRecord?.baseForecast || totalSalesForecast
  const displayWeeklySalesForecast =
    frozenRecord?.weeklySalesForecast || weeklySalesForecast

  const handleSubmit = async () => {
    if (!locationId || !startDate || !userId || isSubmitting || !isToday) return

    setIsSubmitting(true)

    const record: IRecordDataType = {
      adjustment,
      baseForecast: totalSalesForecast,
      adjustedForecast: totalSalesForecast * (1 + adjustment / 100),
      weeklySalesForecast,
      items: prepItemWeekdayDataSource.map((item) => {
        const calculations = calculatePrepItemValues(
          item,
          adjustment,
          onHandValues,
        )

        return {
          prepItemIdCode: item.prepItemIdCode,
          prepItemName: item.prepItemName,
          prepItemUnit: item.prepItemUnit,
          prepBatchType: item.prepBatchType,
          standardUnitsPerBatch: item.standardUnitsPerBatch,
          unitAmountNeeded: item.unitAmountNeeded,
          adjustedUnitAmountNeeded: calculations.adjustedUnitAmountNeeded,
          totalUnitAmountUntilNextPrep: item.totalUnitAmountUntilNextPrep,
          minimumUnit: item.minimumUnit,
          onHandDetails:
            item.prepOnHandDetails?.map((detail) => ({
              batchType: detail.prepBatchType,
              quantity:
                onHandValues[item.prepItemName]?.[detail.prepBatchType] || 0,
              standardUnitsPerBatch: detail.standardUnitsPerBatch,
            })) || [],
          onHand: calculations.onHand,
          onHandUnit: item.onHandUnit,
          needed: calculations.needed,
          prep: calculations.prep,
        }
      }),
      nonPrepItems: prepItemNonPrepDataSource.map((item) => {
        const calculations = calculatePrepItemValues(
          item,
          adjustment,
          onHandValues,
        )

        return {
          prepItemIdCode: item.prepItemIdCode,
          prepItemName: item.prepItemName,
          prepItemUnit: item.prepItemUnit,
          prepBatchType: item.prepBatchType,
          standardUnitsPerBatch: item.standardUnitsPerBatch,
          unitAmountNeeded: item.unitAmountNeeded,
          adjustedUnitAmountNeeded: calculations.adjustedUnitAmountNeeded,
          totalUnitAmountUntilNextPrep: item.totalUnitAmountUntilNextPrep,
          minimumUnit: item.minimumUnit,
          onHandDetails:
            item.prepOnHandDetails?.map((detail) => ({
              batchType: detail.prepBatchType,
              quantity:
                onHandValues[item.prepItemName]?.[detail.prepBatchType] || 0,
              standardUnitsPerBatch: detail.standardUnitsPerBatch,
            })) || [],
          onHand: calculations.onHand,
          onHandUnit: item.onHandUnit,
          needed: calculations.needed,
          prep: calculations.prep,
        }
      }),
      onHandValues,
      // why: the separate prep-layer increase was folded into the visible
      // adjustment, which is already saved above. Persist 0 for backward
      // compatibility with the record shape; it no longer feeds any calculation.
      adjustmentPercent: 0,
    }

    try {
      await submitRecord({
        variables: {
          iLocationId: locationId,
          iReportDate: startDate,
          iExpoUserId: userId,
          iRecord: record,
        },
      })
      setIsSubmitted(true)
      setShowSummaryModal(true)
    } catch (error) {
      console.error('Error submitting prep item record:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    setAdjustment(prepAmountIncreasePercent || 0)
    setOnHandValues({})
    adjustmentEditedRef.current = false
    setIsSubmitted(false)
  }

  const handlePrintPDF = () => {
    // why: include the current delivery-day banner state on the PDF so a
    // printed sheet conveys the same context as the screen (ENG-4627).
    let bannerType: 'delivery' | 'day-before' | null = null
    if (hasDeliveryDays && isDeliveryDay) {
      bannerType = 'delivery'
    } else if (hasDeliveryDays && isDayBeforeDelivery) {
      bannerType = 'day-before'
    }

    printPrepGuide({
      locationName: locationInfo?.displayName || '',
      date: startDateMoment,
      baseForecast: displayBaseForecast,
      adjustment,
      weeklySalesForecast: displayWeeklySalesForecast,
      prepItems: displayPrepItems,
      nonPrepItems: displayNonPrepItems,
      onHandValues,
      banner: bannerType
        ? {
            type: bannerType,
            deliveryDays: storeDeliveryDays,
            activeDayLabel: startDateMoment?.format('ddd').toLowerCase() ?? '',
          }
        : null,
    })
  }

  return (
    <>
      <Spin spinning={prepItemWeekdayLoading || historicalLoading}>
        {/* Delivery Day Banners */}
        {hasDeliveryDays && isDeliveryDay && (
          <DeliveryDayBanner>
            <span style={{ fontSize: 20 }}>&#x1F69A;</span>
            <div>
              <strong>Delivery Day</strong> &mdash; Today is a delivery day.
              Only items listed under Prep Guide are scheduled to be prepped
              today. Prep amounts for Non-Prep items have been redistributed to
              previous days to minimize prep and allow focus on putting away the
              delivery. On-hand counts must still be entered for all items. If
              you are at risk of running out of any product, prep as needed.
              <div style={{ marginTop: 4 }}>
                {storeDeliveryDays.map((d) => (
                  <DayChip
                    key={d}
                    active={
                      startDateMoment?.format('ddd').toLowerCase() ===
                      d.toLowerCase()
                    }
                  >
                    {d}
                  </DayChip>
                ))}{' '}
              </div>
            </div>
          </DeliveryDayBanner>
        )}
        {hasDeliveryDays && isDayBeforeDelivery && !isDeliveryDay && (
          <DayBeforeBanner>
            <span style={{ fontSize: 20 }}>&#x1F4E6;</span>
            <div>
              <strong>Day Before Delivery</strong> &mdash; Tomorrow is a
              delivery day. Prep amounts for items that will not be prepped
              tomorrow have been redistributed to today, so prep quantities may
              be slightly higher than usual.
            </div>
          </DayBeforeBanner>
        )}

        <SalesForecast
          date={startDateMoment?.format('MM/DD/YYYY') || ''}
          weeklySalesForecast={displayWeeklySalesForecast}
          baseForecast={displayBaseForecast}
          adjustment={adjustment}
          globalDefault={prepAmountIncreasePercent}
          onAdjustmentChange={isBeforeToday ? () => {} : handleAdjustmentChange}
          disableInput={isBeforeToday}
        />
        <PrepGuide
          dataSource={displayPrepItems}
          nonPrepDataSource={displayNonPrepItems}
          adjustment={adjustment}
          onHandValues={onHandValues}
          onOnHandChange={isBeforeToday ? () => {} : setOnHandValues}
          disableInput={isBeforeToday}
        />
        <ButtonContainer>
          {isToday && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              <span>✈</span>{' '}
              {isSubmitting
                ? 'Submitting...'
                : historicalRecord || isSubmitted
                ? 'Re-submit'
                : 'Submit'}
            </Button>
          )}
          <Button variant="secondary" onClick={handlePrintPDF}>
            <span>📄</span> Print to PDF
          </Button>
          {isToday && (
            <Button variant="tertiary" onClick={handleReset}>
              <span>↻</span> Reset
            </Button>
          )}
        </ButtonContainer>

        {!isToday && (
          <ButtonContainer>
            <Button
              variant="primary"
              onClick={() =>
                setDate &&
                setDate({
                  id: 'today',
                  type: 'today',
                  parentId: 'root',
                  year: moment().year(),
                  displayName: moment().format('YYYY-MM-DD'),
                  dateRange: {
                    startDate: moment(),
                    startDateStr: moment().format('YYYY-MM-DD'),
                    endDate: moment(),
                    endDateStr: moment().format('YYYY-MM-DD'),
                  },
                })
              }
              style={{ backgroundColor: COLORS.Plum }}
            >
              <span>📅</span> Go back to Today
            </Button>
          </ButtonContainer>
        )}
      </Spin>

      <PrepGuideSummaryModal
        isOpen={showSummaryModal}
        onClose={() => {
          setShowSummaryModal(false)
        }}
        locationName={locationInfo?.displayName || ''}
        date={startDateMoment}
        weeklySalesForecast={displayWeeklySalesForecast}
        baseForecast={displayBaseForecast}
        adjustment={adjustment}
        prepItems={displayPrepItems}
        nonPrepItems={displayNonPrepItems}
        onHandValues={onHandValues}
        isSubmitted={isSubmitted}
      />
    </>
  )
}

export default PrepItemCalculator
